import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, EmptyState } from '@adop/ui';
import {
  CalendarClock,
  Home,
  Printer,
  ScrollText,
  Store,
  TrendingUp,
  UtensilsCrossed,
} from 'lucide-react';
import { formatCents } from '@adop/shared';
import type { AdminOverviewResponse } from '@adop/shared/admin';
import { fetchAdminOverview } from '../../lib/admin-api';
import { useAsync } from '../../lib/useAsync';
import { activityActorLabel, relativeTime } from '../../lib/format';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { LoadingBlock, Skeleton } from '../../components/LoadingState';
import { OrderStatusBadge, PaymentStatusBadge } from '../../components/StatusBadge';

interface StatCard {
  label: string;
  value: string;
  icon: typeof Store;
  hint?: string;
}

function TotalsCards({ totals }: { totals: AdminOverviewResponse['totals'] }) {
  const cards: StatCard[] = [
    { label: 'Restaurants', value: String(totals.restaurants), icon: Store, hint: `${totals.suspended_restaurants} suspended` },
    { label: 'Locations', value: String(totals.locations), icon: Home, hint: `${totals.published_locations} published` },
    { label: 'Orders (30d)', value: String(totals.orders_30d), icon: UtensilsCrossed },
    { label: 'Revenue (30d)', value: formatCents(totals.revenue_30d_cents, { showCents: false }), icon: TrendingUp },
    { label: 'Reservations (30d)', value: String(totals.reservations_30d), icon: CalendarClock },
    {
      label: 'Printers online',
      value: String(totals.printers_online),
      icon: Printer,
      hint: `${totals.active_printers} active`,
    },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <Card key={card.label}>
            <CardContent className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm text-zinc-500">{card.label}</p>
                <p className="mt-1 text-3xl font-bold text-zinc-900">{card.value}</p>
                {card.hint ? <p className="mt-1 text-xs text-zinc-500">{card.hint}</p> : null}
              </div>
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-zinc-100 text-zinc-500">
                <Icon className="h-5 w-5" />
              </span>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function OverviewSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <Card key={index}>
            <CardContent>
              <Skeleton className="h-4 w-24" />
              <Skeleton className="mt-3 h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
      <LoadingBlock />
    </div>
  );
}

export function AdminOverviewPage() {
  const state = useAsync(fetchAdminOverview, []);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Overview"
        description="Platform-wide activity across every restaurant."
      />

      {state.error ? <Alert>{state.error}</Alert> : null}
      {state.loading ? <OverviewSkeleton /> : null}

      {state.data ? (
        <div className="space-y-6">
          <TotalsCards totals={state.data.totals} />

          <Card>
            <CardHeader className="flex items-center justify-between">
              <CardTitle>Recent orders</CardTitle>
              <Link to="/admin/data" className="text-sm font-medium text-zinc-900 underline">
                Browse data
              </Link>
            </CardHeader>
            <div className="overflow-x-auto">
              {state.data.recent_orders.length === 0 ? (
                <EmptyState icon={<UtensilsCrossed className="h-6 w-6" />} title="No orders yet" />
              ) : (
                <table className="w-full min-w-[40rem] text-left text-sm">
                  <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">Order</th>
                      <th className="px-4 py-2 font-medium">Restaurant</th>
                      <th className="px-4 py-2 font-medium">Location</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                      <th className="px-4 py-2 font-medium">Payment</th>
                      <th className="px-4 py-2 text-right font-medium">Total</th>
                      <th className="px-4 py-2 text-right font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {state.data.recent_orders.map((order) => (
                      <tr key={order.id} className="hover:bg-zinc-50">
                        <td className="px-4 py-3 font-medium text-zinc-900">#{order.order_number}</td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/admin/restaurants?q=${encodeURIComponent(order.restaurant_name)}`}
                            className="font-medium text-zinc-900 underline decoration-zinc-300 hover:decoration-zinc-900"
                          >
                            {order.restaurant_name}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-zinc-600">{order.location_name}</td>
                        <td className="px-4 py-3">
                          <OrderStatusBadge status={order.status} />
                        </td>
                        <td className="px-4 py-3">
                          <PaymentStatusBadge status={order.payment_status} />
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-zinc-900">
                          {formatCents(order.total_cents)}
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-zinc-500">
                          {relativeTime(order.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader className="flex items-center justify-between">
              <CardTitle>Recent admin activity</CardTitle>
              <Link to="/admin/audit" className="text-sm font-medium text-zinc-900 underline">
                View audit log
              </Link>
            </CardHeader>
            <CardContent>
              {state.data.recent_audit.length === 0 ? (
                <EmptyState icon={<ScrollText className="h-6 w-6" />} title="No admin activity yet" />
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {state.data.recent_audit.map((entry) => (
                    <li key={entry.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3 text-sm">
                      <span className="font-medium text-zinc-900">{entry.action}</span>
                      <span className="text-zinc-500">{entry.restaurant_name}</span>
                      {entry.target ? (
                        <span className="truncate text-zinc-500">{entry.target}</span>
                      ) : null}
                      <span className="ml-auto text-xs text-zinc-500">
                        {activityActorLabel(entry.actor_user_id, entry.actor_email)} ·{' '}
                        {relativeTime(entry.created_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
